import React from 'react';
import { NavLink } from 'react-router-dom';
import { usePlanGateStore } from '../../store/planGate';
import { isGateHandled, parsePlanGate } from '../../lib/planGates';
import {
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Box,
  Divider,
  Select,
  MenuItem,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PollIcon from '@mui/icons-material/Poll';
import KeyIcon from '@mui/icons-material/Key';
import WebhookIcon from '@mui/icons-material/Webhook';
import TuneIcon from '@mui/icons-material/Tune';
import SettingsIcon from '@mui/icons-material/Settings';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import InsightsIcon from '@mui/icons-material/Insights';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import IntegrationInstructionsIcon from '@mui/icons-material/IntegrationInstructions';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import HubIcon from '@mui/icons-material/Hub';
import PeopleIcon from '@mui/icons-material/People';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import { useWorkspace } from '../../hooks/useWorkspace';
import { ResponseDockLogo } from '../ResponseDockLogo';
import packageJson from '../../../package.json';
import { useAuthStore } from '../../store/auth';

const LANDING_URL = import.meta.env.VITE_LANDING_URL ?? 'http://localhost:5176';
const SYSTEM_VERSION = packageJson.version;

interface NavItem {
  label: string;
  to: string;
  icon: React.ReactNode;
  adminOnly?: boolean;
  systemAdminOnly?: boolean;
  group: 'Overview' | 'Surveys' | 'Delivery' | 'Administration';
}

const NAV_ITEMS: NavItem[] = [
  { label: 'Getting started', to: '/getting-started', icon: <RocketLaunchIcon />, group: 'Overview' },
  { label: 'Dashboard', to: '/dashboard', icon: <DashboardIcon />, group: 'Overview' },
  { label: 'Insights', to: '/intelligence', icon: <InsightsIcon />, group: 'Overview' },
  { label: 'Surveys', to: '/surveys', icon: <PollIcon />, group: 'Surveys' },
  { label: 'AI designer', to: '/surveys/new/ai', icon: <AutoAwesomeIcon />, group: 'Surveys' },
  { label: 'Templates', to: '/templates', icon: <FileCopyIcon />, group: 'Surveys' },
  { label: 'Audience targeting', to: '/triggers', icon: <TuneIcon />, group: 'Delivery' },
  { label: 'Embed setup', to: '/embed-integration', icon: <IntegrationInstructionsIcon />, adminOnly: true, group: 'Delivery' },
  { label: 'Connected apps', to: '/connected-apps', icon: <HubIcon />, group: 'Delivery' },
  { label: 'API keys', to: '/api-keys', icon: <KeyIcon />, group: 'Delivery' },
  { label: 'Webhooks', to: '/webhooks', icon: <WebhookIcon />, group: 'Delivery' },
  { label: 'AI usage', to: '/ai-usage', icon: <ReceiptLongIcon />, adminOnly: true, group: 'Administration' },
  { label: 'Workspace settings', to: '/settings', icon: <SettingsIcon />, group: 'Administration' },
  { label: 'System users', to: '/system/users', icon: <PeopleIcon />, systemAdminOnly: true, group: 'Administration' },
  { label: 'All surveys', to: '/system/surveys', icon: <ManageSearchIcon />, systemAdminOnly: true, group: 'Administration' },
];

interface SidebarProps {
  width: number;
  mobileOpen: boolean;
  onMobileClose: () => void;
}

export function Sidebar({ width, mobileOpen, onMobileClose }: SidebarProps) {
  const { workspaces, workspaceId, setActiveWorkspace, createWorkspace } = useWorkspace();
  const user = useAuthStore((state) => state.user);
  const [wsDialogOpen, setWsDialogOpen] = React.useState(false);
  const [wsName, setWsName] = React.useState('');
  const [wsError, setWsError] = React.useState<string | null>(null);
  const openUpgrade = usePlanGateStore((state) => state.openUpgrade);

  async function handleCreateWorkspace() {
    if (!wsName.trim()) return;
    setWsError(null);
    try {
      await createWorkspace.mutateAsync(wsName.trim());
      setWsDialogOpen(false);
      setWsName('');
    } catch (e) {
      const data = (e as { response?: { data?: { error?: string; code?: string } } }).response?.data;
      const msg = data?.error ?? '';
      const gate = parsePlanGate(e);
      if (gate) {
        // PLAN_LIMIT_REACHED (limit: 'workspaces', limitedByTrial when the trial caused it): the shared
        // upgrade dialog explains it; the API client usually opened it already.
        if (!isGateHandled(e)) openUpgrade(gate);
        setWsDialogOpen(false);
      } else if (msg.includes('licence') || msg.includes('license') || msg.includes('allow')) {
        setWsError('Your license does not allow more workspaces. Please upgrade.');
      } else if (msg.includes('taken') || msg.includes('Slug')) {
        setWsError('A workspace with this name already exists.');
      } else {
        setWsError('Could not create workspace. Please try again.');
      }
    }
  }

  const activeWorkspaceRole = (workspaces.find((workspace) => workspace.id === workspaceId) as { role?: string } | undefined)?.role;
  const navItems = NAV_ITEMS.filter(
    (item) => (!item.systemAdminOnly || user?.isSystemAdmin) && (!item.adminOnly || activeWorkspaceRole === 'SUPER_ADMIN' || activeWorkspaceRole === 'WORKSPACE_OWNER' || activeWorkspaceRole === 'WORKSPACE_ADMIN'),
  );
  const navGroups = (['Overview', 'Surveys', 'Delivery', 'Administration'] as const)
    .map((label) => ({ label, items: navItems.filter((item) => item.group === label) }))
    .filter((group) => group.items.length > 0);

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box
        component="a"
        href={LANDING_URL}
        target="_blank"
        rel="noreferrer"
        aria-label="Open ResponseDock landing page"
        sx={{
          px: 1.5,
          py: 1.25,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          color: 'inherit',
          textDecoration: 'none',
          '&:hover .ResponseDockSidebar-wordmark': {
            opacity: 0.82,
          },
        }}
      >
        <ResponseDockLogo size={26} showText={false} />
        <Box
          className="ResponseDockSidebar-wordmark"
          sx={{
            fontWeight: 700,
            fontSize: 14,
            letterSpacing: '-0.02em',
            background: 'linear-gradient(135deg, #6366F1 0%, #7C3AED 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            lineHeight: 1.2,
            userSelect: 'none',
          }}
        >
          ResponseDock
        </Box>
      </Box>
      <Divider />

      {/* Workspace switcher */}
      {workspaces.length > 0 && (
        <Box sx={{ px: 2, py: 1.5 }}>
          <Select
            size="small"
            fullWidth
            value={workspaceId ?? ''}
            onChange={(e) => {
              const val = e.target.value as string;
              if (val === '__new__') { setWsDialogOpen(true); return; }
              setActiveWorkspace(val);
            }}
            sx={{ fontSize: 13 }}
          >
            {workspaces.map((w) => (
              <MenuItem key={w.id} value={w.id} sx={{ fontSize: 13 }}>
                {w.name}
              </MenuItem>
            ))}
            <Divider />
            <MenuItem value="__new__" sx={{ fontSize: 13, color: 'primary.main', fontWeight: 600 }}>
              + New workspace
            </MenuItem>
          </Select>
          {activeWorkspaceRole && (
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5, px: 0.5 }}>
              Role: <strong>{workspaceRoleLabel(activeWorkspaceRole)}</strong>
            </Typography>
          )}
        </Box>
      )}
      <Divider />

      {/* Navigation */}
      <List sx={{ flexGrow: 1, px: 0.5, py: 0.5, overflowY: 'auto' }}>
        {navGroups.map((group, groupIndex) => <Box key={group.label} sx={{ mt: groupIndex ? 1 : 0 }}>
          <Typography variant="overline" sx={{ color: 'text.secondary', display: 'block', px: 1.2, pt: 0.7, pb: 0.25, fontSize: 10, fontWeight: 800, letterSpacing: '.08em' }}>{group.label}</Typography>
          {group.items.map((item) => (
          <ListItemButton
            key={item.to}
            component={NavLink}
            to={item.to}
            end={item.to === '/surveys'}
            sx={{
              borderRadius: 1.5,
              mb: 0.25,
              minHeight: 36,
              px: 1.2,
              '&.active': {
                bgcolor: 'rgba(6, 182, 212, 0.10)',
                color: 'primary.main',
                boxShadow: '0 0 6px rgba(6, 182, 212, 0.10)',
                '& .MuiListItemIcon-root': { color: 'primary.main' },
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 28, color: 'text.secondary', fontSize: 18 }}>
              {item.icon}
            </ListItemIcon>
            <ListItemText
              primary={item.label}
              slotProps={{ primary: { sx: { fontSize: 13, fontWeight: 500, letterSpacing: 0.01 } } }}
            />
          </ListItemButton>
          ))}
        </Box>)}
      </List>

      <Box sx={{ p: 2, pt: 0 }}>
        <Divider sx={{ mb: 1.25 }} />
        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', px: 0.5, lineHeight: 1.4 }}>
          System version
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 700, px: 0.5, lineHeight: 1.35 }}>
          v{SYSTEM_VERSION}
        </Typography>
      </Box>
    </Box>
  );

  return (
    <>
      {/* Mobile drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onMobileClose}
        ModalProps={{ keepMounted: true }}
        sx={{ display: { xs: 'block', sm: 'none' }, '& .MuiDrawer-paper': { width } }}
      >
        {drawer}
      </Drawer>
      {/* Desktop drawer */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', sm: 'block' },
          '& .MuiDrawer-paper': { width, boxSizing: 'border-box' },
        }}
        open
      >
        {drawer}
      </Drawer>

      {/* Create workspace dialog */}
      <Dialog
        open={wsDialogOpen}
        onClose={() => { setWsDialogOpen(false); setWsName(''); setWsError(null); }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>New workspace</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
            A workspace keeps its own surveys, responses, team, branding, API keys and webhooks separate from your
            other workspaces. Create one for each brand, location, client or team whose feedback should stay apart.
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
            You'll be its owner and can invite people from Workspace settings → Team.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label="Workspace name"
            value={wsName}
            onChange={(e) => setWsName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') void handleCreateWorkspace(); }}
            error={!!wsError}
            helperText={wsError ?? undefined}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setWsDialogOpen(false); setWsName(''); setWsError(null); }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => void handleCreateWorkspace()}
            disabled={!wsName.trim() || createWorkspace.isPending}
            startIcon={createWorkspace.isPending ? <CircularProgress size={16} /> : undefined}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

function workspaceRoleLabel(role: string): string {
  const map: Record<string, string> = {
    WORKSPACE_OWNER: 'Owner',
    WORKSPACE_ADMIN: 'Admin',
    EDITOR: 'Editor',
    VIEWER: 'Viewer',
  };
  return map[role] ?? role;
}
